import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { Button, Card, Input, Progress, Select, Space, Tag, Typography } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import type { AxiosResponse } from "axios";
import { useEffect, useRef, useState } from "react";
import {
    DateRangePicker as ZhDateRangePicker,
    Dialog as ZhDialog,
    Table as ZhTable,
    type TableColumnProps
} from "zhihao-ui-react";

import request from "@/api";
import type { CustomAxiosRequestConfig } from "@/api";
import { DEFAULT_VALUE } from "@/config";
import Permission from "@/views/components/Permission";
import { useUserStore } from "@/stores/react/authStore";

export interface AiCorpusLibraryRow {
    corpusLibrary: {
        id: number | string;
        personName?: string;
        identifyCorpus?: string;
        correctCorpus?: string;
        createdTime?: string;
        mistake?: number | string;
        markState?: number | string;
        audioId?: number | string;
        duration?: number;
        audioSource?: string;
        isPlaying?: boolean;
        showProgress?: boolean;
        progress?: number;
    };
    corpus?: { content?: string };
}

export interface AiCorpusLibraryQuery {
    page: number;
    size: number;
    name?: string;
    corpus?: string;
    mistake?: number;
    state?: number;
    startTime?: string;
    endTime?: string;
    sortKey?: string;
    sortOrder?: string;
}

export interface AiCorpusLibraryPageResult {
    list?: AiCorpusLibraryRow[];
    total?: number;
}

export interface AiCorpusLibraryFile {
    path: string;
}

export interface AiCorpusLibraryPageProps {
    loadLibrary?: (query: AiCorpusLibraryQuery) => Promise<AiCorpusLibraryPageResult>;
    loadFile?: (id: number) => Promise<AiCorpusLibraryFile>;
    accurate?: (id: number) => Promise<unknown>;
    correct?: (id: number, correctCorpus: string) => Promise<unknown>;
    remove?: (id: number) => Promise<unknown>;
    exportLibrary?: (query: AiCorpusLibraryQuery) => Promise<void>;
}

// 与旧 Vue 接口契约保持默认每页二十条，避免首屏请求和行为用例的分页基线发生变化。
const defaultQuery: AiCorpusLibraryQuery = { page: 1, size: 20 };

/** 复用 Vue AI语料库分页接口。 */
export async function loadAiCorpusLibrary(query: AiCorpusLibraryQuery): Promise<AiCorpusLibraryPageResult> {
    const response = await request.service.get<AiCorpusLibraryPageResult>("/admin/corpus-library/paging", {
        params: query
    });
    return response.data;
}

/** 复用 Vue 文件服务获取音频地址。 */
export async function loadAiCorpusLibraryFile(id: number): Promise<AiCorpusLibraryFile> {
    const response = await request.service.get<AiCorpusLibraryFile>(`/dataService/admin/file/${id}`);
    return response.data;
}

export async function accurateAiCorpusLibrary(id: number): Promise<unknown> {
    const response = await request.service.put(`/admin/corpus-library/accurate/${id}`);
    return response.data;
}

export async function correctAiCorpusLibrary(id: number, correctCorpus: string): Promise<unknown> {
    const response = await request.service.put(
        `/admin/corpus-library/correct/${id}?correctCorpus=${encodeURIComponent(correctCorpus)}`
    );
    return response.data;
}

export async function deleteAiCorpusLibrary(id: number): Promise<unknown> {
    const response = await request.service.delete(`/admin/corpus-library/${id}`);
    return response.data;
}

/** 将 Vue 导出的语料库 Blob 转成浏览器下载，并确保临时资源始终释放。 */
function downloadAiCorpusLibraryBlob(blob: Blob, filename: string): void {
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.setAttribute("download", filename);
    document.body.appendChild(link);

    try {
        link.click();
    } finally {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(objectUrl);
    }
}

/** 复用 Vue AI 语料库导出接口，导出文件使用当天的默认中文文件名。 */
export async function exportAiCorpusLibrary(query: AiCorpusLibraryQuery): Promise<void> {
    const response = await request.service.get<Blob, AxiosResponse<Blob>>("/admin/corpus-library/export", {
        params: query,
        responseType: "blob",
        returnRawResponse: true,
        timeout: 600000
    } as CustomAxiosRequestConfig);
    const currentDate = new Date();
    const filename = `${currentDate.getFullYear()}-${currentDate.getMonth() + 1}-${currentDate.getDate()} AI语料库.xlsx`;
    downloadAiCorpusLibraryBlob(response.data, filename);
}

/** 将 Vue 语料库筛选字段转换为分页请求参数。 */
export function buildAiCorpusLibraryQuery(values: {
    name?: string;
    corpus?: string;
    mistake?: number;
    state?: number;
    createdTime?: [string, string];
    sortKey?: string;
    sortOrder?: string;
}): AiCorpusLibraryQuery {
    const query: AiCorpusLibraryQuery = { ...defaultQuery };
    if (values.name) query.name = values.name;
    if (values.corpus) query.corpus = values.corpus;
    if (values.mistake !== undefined) query.mistake = values.mistake;
    if (values.state !== undefined) query.state = values.state;
    if (values.createdTime?.[0] && values.createdTime[1]) {
        query.startTime = `${values.createdTime[0]} 00:00:00`;
        query.endTime = `${values.createdTime[1]} 23:59:59`;
    }
    if (values.sortKey) query.sortKey = values.sortKey;
    if (values.sortOrder) query.sortOrder = values.sortOrder;
    return query;
}

function isUnmarked(row: AiCorpusLibraryRow): boolean {
    return Number(row.corpusLibrary.markState) === 1;
}

function formatDate(value?: string): string {
    return value ? value.replace("T", " ").slice(0, 19) : DEFAULT_VALUE;
}

/** AI语料库列表，保留 Vue 的字段、筛选和标记操作。 */
export function AiCorpusLibraryPage({
    loadLibrary = loadAiCorpusLibrary,
    loadFile = loadAiCorpusLibraryFile,
    accurate = accurateAiCorpusLibrary,
    correct = correctAiCorpusLibrary,
    remove = deleteAiCorpusLibrary,
    exportLibrary = exportAiCorpusLibrary
}: AiCorpusLibraryPageProps) {
    const banButton = useUserStore(state => state.banButton);
    const [rows, setRows] = useState<AiCorpusLibraryRow[]>([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState<AiCorpusLibraryQuery>(defaultQuery);
    const [name, setName] = useState("");
    const [corpus, setCorpus] = useState("");
    const [mistake, setMistake] = useState<number | undefined>();
    const [state, setState] = useState<number | undefined>();
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [reloadToken, setReloadToken] = useState(0);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [correctRow, setCorrectRow] = useState<AiCorpusLibraryRow | null>(null);
    const [correctCorpus, setCorrectCorpus] = useState("");
    const [currentAudioId, setCurrentAudioId] = useState<number | string | null>(null);
    const [correctionAudioPlaying, setCorrectionAudioPlaying] = useState(false);
    const [correctionAudioProgress, setCorrectionAudioProgress] = useState(0);
    const audioRef = useRef<HTMLAudioElement | null>(null);

    useEffect(() => {
        let disposed = false;
        void loadLibrary(query)
            .then(result => {
                if (disposed) return;
                setRows(
                    (result.list || []).map(row => ({
                        ...row,
                        corpusLibrary: {
                            ...row.corpusLibrary,
                            isPlaying: false,
                            // 旧 Vue 列表加载后默认展示音频进度条，保持 0% 初始状态可见。
                            showProgress: true,
                            progress: 0
                        }
                    }))
                );
                setTotal(result.total || 0);
                setErrorMessage(null);
            })
            .catch(() => {
                if (disposed) return;
                setRows([]);
                setTotal(0);
                setErrorMessage("AI语料库加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadLibrary, query, reloadToken]);

    const runAction = async (action: (id: number) => Promise<unknown>, row: AiCorpusLibraryRow) => {
        try {
            await action(Number(row.corpusLibrary.id));
            setReloadToken(value => value + 1);
        } catch {
            setErrorMessage("AI语料库操作失败");
        }
    };

    const submitCorrection = async () => {
        if (!correctRow) return;
        try {
            await correct(Number(correctRow.corpusLibrary.id), correctCorpus);
            setCorrectRow(null);
            setReloadToken(value => value + 1);
        } catch {
            setErrorMessage("AI语料库操作失败");
        }
    };

    /** 停止修正弹窗音频并清空进度，避免关闭后继续占用共享 audio 元素。 */
    const stopCorrectionAudio = () => {
        setCorrectionAudioPlaying(false);
        setCorrectionAudioProgress(0);
    };

    /** 按旧 Vue 修正弹窗契约加载并播放当前语料音频。 */
    const toggleCorrectionAudio = async () => {
        const audioId = correctRow?.corpusLibrary.audioId;
        const audio = audioRef.current;
        if (audioId === undefined || audio === null) return;

        if (correctionAudioPlaying) {
            audio.pause();
            stopCorrectionAudio();
            return;
        }

        if (currentAudioId !== null) {
            audio.pause();
            resetAudioRow(currentAudioId);
            setCurrentAudioId(null);
        }

        try {
            const file = await loadFile(Number(audioId));
            audio.src = file.path;
            await audio.play();
            setCorrectionAudioPlaying(true);
        } catch {
            stopCorrectionAudio();
            setErrorMessage("音频播放失败");
        }
    };

    /** 将指定音频行状态恢复到未播放状态。 */
    const resetAudioRow = (audioId: number | string) => {
        setRows(currentRows =>
            currentRows.map(item =>
                item.corpusLibrary.audioId === audioId
                    ? {
                          ...item,
                          corpusLibrary: { ...item.corpusLibrary, isPlaying: false, showProgress: false, progress: 0 }
                      }
                    : item
            )
        );
    };

    /** 按 Vue 文件服务契约加载音频并切换播放/暂停状态。 */
    const toggleAudio = async (row: AiCorpusLibraryRow) => {
        const audioId = row.corpusLibrary.audioId;
        const audio = audioRef.current;
        if (audioId === undefined || audio === null) return;

        if (currentAudioId !== null && currentAudioId !== audioId) {
            audio.pause();
            resetAudioRow(currentAudioId);
        }

        if (currentAudioId === audioId && row.corpusLibrary.isPlaying) {
            audio.pause();
            setRows(currentRows =>
                currentRows.map(item =>
                    item.corpusLibrary.id === row.corpusLibrary.id
                        ? { ...item, corpusLibrary: { ...item.corpusLibrary, isPlaying: false } }
                        : item
                )
            );
            return;
        }

        try {
            const file = await loadFile(Number(audioId));
            audio.src = file.path;
            setCurrentAudioId(audioId);
            setRows(currentRows =>
                currentRows.map(item =>
                    item.corpusLibrary.id === row.corpusLibrary.id
                        ? {
                              ...item,
                              corpusLibrary: {
                                  ...item.corpusLibrary,
                                  audioSource: file.path,
                                  isPlaying: true,
                                  showProgress: true
                              }
                          }
                        : item
                )
            );
            await audio.play();
        } catch {
            resetAudioRow(audioId);
            setCurrentAudioId(null);
            setErrorMessage("音频播放失败");
        }
    };

    /** 使用 Vue 返回的毫秒时长计算播放百分比，避免依赖浏览器音频元数据。 */
    const updateAudioProgress = () => {
        if (correctionAudioPlaying && correctRow && audioRef.current) {
            const duration = Number(correctRow.corpusLibrary.duration || 0) / 1000;
            const progress = duration > 0 ? Math.min(100, (audioRef.current.currentTime / duration) * 100) : 0;
            setCorrectionAudioProgress(progress);
            return;
        }
        if (currentAudioId === null || !audioRef.current) return;
        const currentTime = audioRef.current.currentTime;
        setRows(currentRows =>
            currentRows.map(item => {
                if (item.corpusLibrary.audioId !== currentAudioId) return item;
                const duration = Number(item.corpusLibrary.duration || 0) / 1000;
                const progress = duration > 0 ? Math.min(100, (currentTime / duration) * 100) : 0;
                return { ...item, corpusLibrary: { ...item.corpusLibrary, progress, showProgress: true } };
            })
        );
    };

    /** 音频播放结束后清理当前行状态，保持列表可再次播放。 */
    const finishAudio = () => {
        if (correctionAudioPlaying) stopCorrectionAudio();
        if (currentAudioId !== null) resetAudioRow(currentAudioId);
        setCurrentAudioId(null);
    };

    /** 打开修正弹窗时重置弹窗音频状态，并回显识别语料。 */
    const openCorrection = (row: AiCorpusLibraryRow) => {
        stopCorrectionAudio();
        setCorrectRow(row);
        setCorrectCorpus(row.corpusLibrary.identifyCorpus || "");
    };

    /** 按 Vue 列表导出契约导出当前筛选结果，并保留列表状态供用户继续操作。 */
    const handleExport = async () => {
        try {
            await exportLibrary({ ...query, page: 1, size: 10000 });
        } catch {
            setErrorMessage("AI语料库导出失败");
        }
    };

    // 用 zhihao-ui-react 列契约承接旧 Vue 语料库字段、音频和权限操作插槽。
    const columns: TableColumnProps<AiCorpusLibraryRow>[] = [
        {
            key: "personName",
            label: "人员",
            width: 92,
            prop: "corpusLibrary",
            render: ({ row }) => row.corpusLibrary.personName || DEFAULT_VALUE
        },
        {
            key: "corpus",
            label: "训练语料",
            minWidth: 204,
            prop: "corpus",
            render: ({ row }) => row.corpus?.content || DEFAULT_VALUE
        },
        {
            key: "identifyCorpus",
            label: "识别语料",
            width: 360,
            prop: "corpusLibrary.identifyCorpus",
            render: ({ row }) => row.corpusLibrary.identifyCorpus || DEFAULT_VALUE
        },
        {
            key: "correctCorpus",
            label: "修正语料",
            minWidth: 204,
            prop: "corpusLibrary",
            render: ({ row }) => row.corpusLibrary.correctCorpus || DEFAULT_VALUE
        },
        {
            key: "createdTime",
            label: "创建时间",
            width: 148,
            prop: "corpusLibrary.createdTime",
            sortable: true,
            render: ({ row }) => formatDate(row.corpusLibrary.createdTime)
        },
        {
            key: "markState",
            label: "状态",
            width: 78,
            prop: "corpusLibrary",
            render: ({ row }) => <Tag>{isUnmarked(row) ? "未标记" : "已标记"}</Tag>
        },
        {
            key: "audioId",
            label: "音频",
            minWidth: 176,
            prop: "corpusLibrary",
            render: ({ row }) =>
                row.corpusLibrary.audioId === undefined ? (
                    DEFAULT_VALUE
                ) : (
                    <Space>
                        <Button
                            aria-label={`${row.corpusLibrary.isPlaying ? "暂停" : "播放"} ${row.corpusLibrary.id}`}
                            type="link"
                            onClick={() => void toggleAudio(row)}
                        >
                            {row.corpusLibrary.isPlaying ? "暂停" : "播放"}
                        </Button>
                        {row.corpusLibrary.showProgress ? (
                            <div
                                aria-label={`语料音频进度 ${row.corpusLibrary.id}`}
                                role="progressbar"
                                aria-valuenow={row.corpusLibrary.progress || 0}
                            >
                                <Progress percent={row.corpusLibrary.progress || 0} showInfo={false} />
                            </div>
                        ) : null}
                    </Space>
                )
        },
        {
            key: "operation",
            label: "操作",
            fixed: "right",
            width: 230,
            prop: "corpusLibrary",
            render: ({ row }) => (
                <Space>
                    {isUnmarked(row) ? (
                        <Permission anyOf={["ROLE_ASSISTANTS_CORPUS_W"]}>
                            <Button
                                aria-label={`准确 ${row.corpusLibrary.id}`}
                                disabled={banButton}
                                type="link"
                                onClick={() => void runAction(accurate, row)}
                            >
                                准确
                            </Button>
                        </Permission>
                    ) : null}
                    <Permission anyOf={["ROLE_ASSISTANTS_CORPUS_W"]}>
                        <Button
                            aria-label={`修正 ${row.corpusLibrary.id}`}
                            disabled={banButton}
                            type="link"
                            onClick={() => openCorrection(row)}
                        >
                            修正
                        </Button>
                    </Permission>
                    <Permission anyOf={["ROLE_ASSISTANTS_CORPUS_W"]}>
                        <Button
                            aria-label={`删除 ${row.corpusLibrary.id}`}
                            disabled={banButton}
                            type="link"
                            onClick={() => void runAction(remove, row)}
                        >
                            删除
                        </Button>
                    </Permission>
                </Space>
            )
        }
    ];

    return (
        <section className="flex h-full min-h-full flex-col" aria-label="ai-corpus-library-page">
            <Card className="flex h-full flex-1 flex-col [&_.ant-card-body]:flex [&_.ant-card-body]:min-h-0 [&_.ant-card-body]:flex-1 [&_.ant-card-body]:flex-col">
                <ZhTable<AiCorpusLibraryRow>
                    data={rows}
                    columns={normalizeOperationColumns(columns, rows)}
                    pagination={false}
                    rowKey={row => String(row.corpusLibrary.id)}
                    onSortChange={({ prop, order }) => {
                        // 将旧 Vue 创建时间排序映射为分页接口的 created_time 参数。
                        if (prop !== "corpusLibrary.createdTime") return;
                        setQuery(current => {
                            const nextQuery = { ...current, page: 1 };
                            if (!order) {
                                delete nextQuery.sortKey;
                                delete nextQuery.sortOrder;
                            } else {
                                nextQuery.sortKey = "created_time";
                                nextQuery.sortOrder = order === "ascending" ? "asc" : "desc";
                            }
                            return nextQuery;
                        });
                    }}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title className="mb-4 mt-0" level={4}>
                                AI语料库
                            </Typography.Title>

                            {errorMessage ? (
                                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                            ) : null}

                            <Space className="mb-4" wrap>
                                <Input
                                    aria-label="人员关键词"
                                    placeholder="请输入人员名称搜索"
                                    value={name}
                                    onChange={event => setName(event.target.value)}
                                />
                                <Input
                                    aria-label="训练语料关键词"
                                    placeholder="请输入训练语料关键词搜索"
                                    value={corpus}
                                    onChange={event => setCorpus(event.target.value)}
                                />
                                <ZhDateRangePicker
                                    endPlaceholder="创建结束日期"
                                    format="YYYY-MM-DD"
                                    rangeSeparator="-"
                                    startPlaceholder="创建开始日期"
                                    type="daterange"
                                    value={[startDate || null, endDate || null]}
                                    valueFormat="YYYY-MM-DD"
                                    onChange={(_value, formattedValue) => {
                                        setStartDate(formattedValue?.[0] || "");
                                        setEndDate(formattedValue?.[1] || "");
                                    }}
                                />
                                <Select
                                    aria-label="识别错误筛选"
                                    allowClear
                                    placeholder="是否识别有误"
                                    value={mistake}
                                    options={[
                                        { label: "否", value: 0 },
                                        { label: "是", value: 1 }
                                    ]}
                                    onChange={value => setMistake(value)}
                                />
                                <Select
                                    aria-label="语料库状态"
                                    allowClear
                                    placeholder="请选择状态"
                                    value={state}
                                    options={[
                                        { label: "已标记", value: 0 },
                                        { label: "未标记", value: 1 }
                                    ]}
                                    onChange={value => setState(value)}
                                />
                                <Button
                                    aria-label="查询"
                                    type="primary"
                                    onClick={() =>
                                        setQuery(
                                            buildAiCorpusLibraryQuery({
                                                name,
                                                corpus,
                                                mistake,
                                                state,
                                                createdTime: [startDate, endDate],
                                                sortKey: query.sortKey,
                                                sortOrder: query.sortOrder
                                            })
                                        )
                                    }
                                >
                                    查询
                                </Button>
                                <Button
                                    aria-label="重置"
                                    onClick={() => {
                                        setName("");
                                        setCorpus("");
                                        setMistake(undefined);
                                        setState(undefined);
                                        setStartDate("");
                                        setEndDate("");
                                        setQuery(defaultQuery);
                                    }}
                                >
                                    重置
                                </Button>
                                <Permission anyOf={["ROLE_ASSISTANTS_CORPUS_W"]}>
                                    <Button aria-label="导出" disabled={banButton} onClick={() => void handleExport()}>
                                        导出
                                    </Button>
                                </Permission>
                            </Space>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                hideOnSinglePage
                                onChange={(page, size) => setQuery(current => ({ ...current, page, size }))}
                            />
                        </div>
                    }
                />
                <audio ref={audioRef} aria-hidden="true" onEnded={finishAudio} onTimeUpdate={updateAudioProgress} />
                <ZhDialog
                    open={Boolean(correctRow)}
                    title="修正"
                    onCancel={() => setCorrectRow(null)}
                    width={500}
                    footer={
                        <Space>
                            <Button onClick={() => setCorrectRow(null)}>取消</Button>
                            <Button type="primary" disabled={banButton} onClick={() => void submitCorrection()}>
                                确认修正
                            </Button>
                        </Space>
                    }
                >
                    <Space orientation="vertical" className="w-full">
                        <div>训练语料：{correctRow?.corpus?.content || DEFAULT_VALUE}</div>
                        <Space>
                            <Button
                                aria-label={`${correctionAudioPlaying ? "暂停" : "播放"}修正音频 ${correctRow?.corpusLibrary.id}`}
                                disabled={correctRow?.corpusLibrary.audioId === undefined}
                                onClick={() => void toggleCorrectionAudio()}
                            >
                                {correctionAudioPlaying ? "暂停修正音频" : "播放修正音频"}
                            </Button>
                            {correctionAudioPlaying ? (
                                <div
                                    aria-label={`修正语料音频进度 ${correctRow?.corpusLibrary.id}`}
                                    role="progressbar"
                                    aria-valuenow={correctionAudioProgress}
                                >
                                    <Progress percent={correctionAudioProgress} showInfo={false} />
                                </div>
                            ) : null}
                        </Space>
                        <div>识别语料：{correctRow?.corpusLibrary.identifyCorpus || DEFAULT_VALUE}</div>
                        <Input
                            aria-label="修正语料输入"
                            maxLength={100}
                            value={correctCorpus}
                            onChange={event => setCorrectCorpus(event.target.value)}
                        />
                    </Space>
                </ZhDialog>
            </Card>
        </section>
    );
}
